export const STORAGE_KEY = "hi-wizard-state-v3";

export const MEMBERS = [
  { key: "self", label: "Self" },
  { key: "wife", label: "Spouse" },
  { key: "son", label: "Son" },
  { key: "daughter", label: "Daughter" },
  { key: "father", label: "Father" },
  { key: "mother", label: "Mother" },
];

export const IMPLICIT_GENDER = {
  wife: "female",
  son: "male",
  daughter: "female",
  father: "male",
  mother: "female",
};

export const ID_TYPES = ["PAN", "Aadhaar", "Passport"];

export const OCCUPATIONS = [
  "Salaried",
  "Self-Employed / Business",
  "Professional (Doctor, CA, Lawyer, etc.)",
  "Homemaker",
  "Student",
  "Retired",
  "Unemployed",
  "Other",
];

export const QUALIFICATIONS = [
  "Below 10th",
  "10th Pass",
  "12th Pass",
  "Diploma",
  "Graduate",
  "Post Graduate",
  "Doctorate",
  "Other",
];

export const INCOME_SLABS = [
  "Below ₹2.5 Lakh",
  "₹2.5 – 5 Lakh",
  "₹5 – 10 Lakh",
  "₹10 – 25 Lakh",
  "₹25 Lakh – 1 Crore",
  "Above ₹1 Crore",
];

export const NOMINEE_RELATIONS = [
  "Spouse",
  "Son",
  "Daughter",
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Other",
];

const emptyInsured = (genderDefault = "male") => ({
  name: "",
  dob: "",
  gender: genderDefault,
  heightCm: "",
  weightKg: "",
  preExisting: {
    hasCondition: false,
    condition: "",
    since: "",
    onMedication: false,
    medicineName: "",
    dosesPerDay: "",
  },
  lifestyle: {
    drinking: { status: "no", since: "", quantity: "" },
    smoking: { status: "no", since: "", sticksPerDay: "" },
    chewing: { status: "no", since: "", product: "" },
  },
});

export const emptyInsuredForMember = (mKey) =>
  emptyInsured(IMPLICIT_GENDER[mKey] || "male");

export const initialState = {
  step: 0,
  selectedMembers: [],
  insured: {},
  proposer: {
    name: "",
    mobile: "",
    email: "",
    address: "",
    pincode: "",
    city: "",
    occupation: "",
    qualification: "",
    annualIncome: "",
    alternateMobile: "",
  },
  bank: {
    accountNumber: "",
    ifscCode: "",
  },
  kyc: {
    panNumber: "",
    panCardFileName: "",
    panCardFileType: "",
    panCardBase64: "",
    aadhaarNumber: "",
    aadhaarCardFileName: "",
    aadhaarCardFileType: "",
    aadhaarCardBase64: "",
    nominee: { name: "", relation: "", dob: "" },
    medicalDeclaration: "none",
    medicalConditions: "",
    consent: false,
    portEnabled: false,
    prevPolicyFileName: "",
    prevPolicyFileType: "",
    prevPolicyBase64: "",
  },
  referenceNumber: "",
};

export const TOTAL_STEPS = 6;

// ---------- Regex ----------
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_RE = /^[6-9]\d{9}$/;
const PIN_RE = /^\d{6}$/;
const PAN_RE = /^[A-Z]{5}\d{4}[A-Z]$/;
const AADHAAR_RE = /^\d{12}$/;
const PASSPORT_RE = /^[A-Z][0-9]{7}$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_RE = /^\d{9,18}$/;

// ---------- Helpers ----------
export function ageFromDob(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const AGE_RANGE = {
  self: { min: 18, max: 99, label: "Self must be aged 18–99." },
  wife: { min: 18, max: 99, label: "Spouse must be aged 18–99." },
  son: { min: 0, max: 30, label: "Son must be aged 0–30." },
  daughter: { min: 0, max: 30, label: "Daughter must be aged 0–30." },
  father: { min: 40, max: 120, label: "Father must be aged 40–120." },
  mother: { min: 40, max: 120, label: "Mother must be aged 40–120." },
};
const MIN_PARENT_GAP = 15;

// ---------- Per-step validators (small, single-purpose) ----------

function validateMemberSelection(state) {
  const errors = {};
  if (!state.selectedMembers.length)
    errors.selectedMembers = "Select at least one family member.";
  return errors;
}

function validatePersonalFields(mKey, ins) {
  const errs = {};
  if (!ins.name || !ins.name.trim()) errs[`${mKey}-name`] = "Name is required.";

  if (!ins.dob) errs[`${mKey}-dob`] = "Date of birth is required.";
  else {
    const age = ageFromDob(ins.dob);
    if (age === null || age < 0 || age > 120)
      errs[`${mKey}-dob`] = "Enter a valid date of birth.";
    else {
      const range = AGE_RANGE[mKey];
      if (range && (age < range.min || age > range.max))
        errs[`${mKey}-dob`] = range.label;
    }
  }
  if (!ins.gender) errs[`${mKey}-gender`] = "Select gender.";

  const h = Number(ins.heightCm);
  if (ins.heightCm === "" || ins.heightCm === undefined || ins.heightCm === null)
    errs[`${mKey}-height`] = "Height is required.";
  else if (Number.isNaN(h) || h < 30 || h > 250)
    errs[`${mKey}-height`] = "Height must be 30–250 cm.";

  const w = Number(ins.weightKg);
  if (ins.weightKg === "" || ins.weightKg === undefined || ins.weightKg === null)
    errs[`${mKey}-weight`] = "Weight is required.";
  else if (Number.isNaN(w) || w < 2 || w > 300)
    errs[`${mKey}-weight`] = "Weight must be 2–300 kg.";

  return errs;
}

function validatePreExisting(mKey, pe) {
  const errs = {};
  if (!pe || !pe.hasCondition) return errs;
  if (!pe.condition || !pe.condition.trim())
    errs[`${mKey}-condition`] = "Please specify the condition.";
  if (!pe.since || !pe.since.trim())
    errs[`${mKey}-since`] = "Please mention since when.";
  if (pe.onMedication) {
    if (!pe.medicineName || !pe.medicineName.trim())
      errs[`${mKey}-medicine`] = "Please mention the medicine.";
    const d = Number(pe.dosesPerDay);
    if (pe.dosesPerDay === "" || Number.isNaN(d) || d < 1 || d > 20)
      errs[`${mKey}-doses`] = "Doses per day must be 1–20.";
  }
  return errs;
}

const LIFESTYLE_MSG = {
  drinking: { qtyKey: "quantity", qtyMsg: "Please mention quantity." },
  smoking: { qtyKey: "sticksPerDay", qtyMsg: "Please mention sticks/day." },
  chewing: { qtyKey: "product", qtyMsg: "Please mention product name." },
};

function validateLifestyle(mKey, ls) {
  const errs = {};
  if (!ls) return errs;
  for (const lk of Object.keys(LIFESTYLE_MSG)) {
    const l = ls[lk] || {};
    if (l.status !== "yes") continue;
    const { qtyKey, qtyMsg } = LIFESTYLE_MSG[lk];
    if (!l.since || !l.since.trim())
      errs[`${mKey}-${lk}-since`] = "Please mention since when.";
    if (!l[qtyKey] || !String(l[qtyKey]).trim())
      errs[`${mKey}-${lk}-qty`] = qtyMsg;
  }
  return errs;
}

function validateCrossMemberAges(state, existingErrors) {
  const errs = {};
  const ageOf = (k) => {
    const dob = state.insured[k]?.dob;
    return dob ? ageFromDob(dob) : null;
  };
  const has = (k) =>
    state.selectedMembers.includes(k) && ageOf(k) !== null;

  const isSetForMember = (k) =>
    existingErrors[`${k}-dob`] || errs[`${k}-dob`];

  // Parents must be older than everyone else by MIN_PARENT_GAP
  ["father", "mother"].forEach((parent) => {
    if (!has(parent) || isSetForMember(parent)) return;
    ["self", "wife", "son", "daughter"].forEach((child) => {
      if (!has(child) || isSetForMember(child)) return;
      if (ageOf(parent) < ageOf(child) + MIN_PARENT_GAP) {
        errs[`${parent}-dob`] = `${
          parent === "father" ? "Father" : "Mother"
        } must be at least ${MIN_PARENT_GAP} years older than ${
          child === "self" ? "self" : child === "wife" ? "spouse" : child
        }.`;
      }
    });
  });

  // Self/spouse must be older than kids by MIN_PARENT_GAP
  ["son", "daughter"].forEach((child) => {
    if (!has(child) || isSetForMember(child)) return;
    if (has("self") && !isSetForMember("self")) {
      if (ageOf("self") < ageOf(child) + MIN_PARENT_GAP) {
        errs[`${child}-dob`] = `${
          child === "son" ? "Son" : "Daughter"
        } must be at least ${MIN_PARENT_GAP} years younger than self.`;
      }
    }
    if (
      has("wife") &&
      !isSetForMember("wife") &&
      !isSetForMember(child) &&
      !errs[`${child}-dob`]
    ) {
      if (ageOf("wife") < ageOf(child) + MIN_PARENT_GAP) {
        errs[`${child}-dob`] = `${
          child === "son" ? "Son" : "Daughter"
        } must be at least ${MIN_PARENT_GAP} years younger than spouse.`;
      }
    }
  });

  return errs;
}

function validateInsuredDetails(state) {
  let errors = {};
  state.selectedMembers.forEach((mKey) => {
    const ins = state.insured[mKey] || {};
    errors = {
      ...errors,
      ...validatePersonalFields(mKey, ins),
      ...validatePreExisting(mKey, ins.preExisting),
      ...validateLifestyle(mKey, ins.lifestyle),
    };
  });
  errors = { ...errors, ...validateCrossMemberAges(state, errors) };
  return errors;
}

// Validate just one member (used by the per-member inner pager on step 1).
// Cross-member age rules are kept only for this member, checked against the
// other members already filled in.
export function validateSingleInsured(state, mKey) {
  const ins = state.insured[mKey] || {};
  const errs = {
    ...validatePersonalFields(mKey, ins),
    ...validatePreExisting(mKey, ins.preExisting),
    ...validateLifestyle(mKey, ins.lifestyle),
  };
  const cross = validateCrossMemberAges(state, errs);
  Object.keys(cross).forEach((k) => {
    if (k.startsWith(`${mKey}-`)) errs[k] = cross[k];
  });
  return errs;
}

function validateProposer(state) {
  const errors = {};
  const p = state.proposer;
  if (!p.name.trim()) errors.name = "Proposer name is required.";
  if (!MOBILE_RE.test(p.mobile))
    errors.mobile = "Enter a valid 10-digit Indian mobile number.";
  if (!EMAIL_RE.test(p.email)) errors.email = "Enter a valid email.";
  if (!p.address.trim()) errors.address = "Address is required.";
  if (!PIN_RE.test(p.pincode))
    errors.pincode = "Enter a valid 6-digit PIN code.";
  if (!p.occupation) errors.occupation = "Select an occupation.";
  if (!p.qualification) errors.qualification = "Select qualification.";
  if (!p.annualIncome) errors.annualIncome = "Select annual income.";
  if (p.alternateMobile && !MOBILE_RE.test(p.alternateMobile))
    errors.alternateMobile = "Enter a valid 10-digit mobile or leave blank.";
  if (p.alternateMobile && p.alternateMobile === p.mobile)
    errors.alternateMobile = "Alternate must differ from primary mobile.";
  return errors;
}

function validateBank(state) {
  const errors = {};
  const b = state.bank;
  if (!ACCOUNT_RE.test(b.accountNumber))
    errors.accountNumber = "Account number must be 9–18 digits.";
  if (!IFSC_RE.test(b.ifscCode.toUpperCase()))
    errors.ifscCode = "IFSC format: ABCD0XXXXXX.";
  return errors;
}

function validateKYC(state) {
  const errors = {};
  const k = state.kyc;

  if (!k.panNumber) errors.panNumber = "PAN number is required.";
  else if (!PAN_RE.test(k.panNumber.toUpperCase()))
    errors.panNumber = "PAN format: ABCDE1234F.";
  if (!k.panCardBase64) errors.panCard = "Please upload the PAN card.";

  if (!k.aadhaarNumber) errors.aadhaarNumber = "Aadhaar number is required.";
  else if (!AADHAAR_RE.test(k.aadhaarNumber))
    errors.aadhaarNumber = "Aadhaar must be 12 digits.";
  if (!k.aadhaarCardBase64) errors.aadhaarCard = "Please upload the Aadhaar card.";

  if (!k.nominee.name.trim()) errors.nomineeName = "Nominee name is required.";
  if (!k.nominee.relation) errors.nomineeRelation = "Select nominee relation.";
  if (!k.nominee.dob) errors.nomineeDob = "Nominee DOB is required.";
  else {
    const age = ageFromDob(k.nominee.dob);
    if (age === null || age < 0 || age > 120)
      errors.nomineeDob = "Enter a valid DOB.";
  }

  if (k.medicalDeclaration === "existing" && !k.medicalConditions.trim())
    errors.medicalConditions = "Please describe the condition(s).";

  if (k.portEnabled && !k.prevPolicyBase64)
    errors.prevPolicy = "Please upload your previous policy document.";

  if (!k.consent) errors.consent = "You must authorize to proceed.";
  return errors;
}

// ---------- Facade ----------
const STEP_VALIDATORS = {
  0: validateMemberSelection,
  1: validateInsuredDetails,
  2: validateProposer,
  3: validateBank,
  4: () => ({}), // Review has no validation
  5: validateKYC,
};

export function validateStep(step, state) {
  const validator = STEP_VALIDATORS[step];
  return validator ? validator(state) : {};
}
